import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { transcriptsApi } from '@/api/transcripts'
import type { ListTranscriptsRequest } from '@/types'

export function useTranscripts(params?: ListTranscriptsRequest) {
  return useQuery({
    queryKey: ['transcripts', params],
    queryFn: () => transcriptsApi.list(params),
  })
}

export function useTranscript(id: string) {
  return useQuery({
    queryKey: ['transcript', id],
    queryFn: () => transcriptsApi.getById(id),
    enabled: !!id,
  })
}

export function useTranscriptAudioUrl(id: string) {
  return useQuery({
    queryKey: ['transcript-audio', id],
    queryFn: () => transcriptsApi.getAudioUrl(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 50, // URL valid for ~1 hour, refresh at 50 min
  })
}

export function useStats(params?: {
  period?: 'hour' | 'day' | 'week'
  start_date?: string
  end_date?: string
  room_name?: string
}) {
  return useQuery({
    queryKey: ['stats', params],
    queryFn: () => transcriptsApi.getStats(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export function useDeleteTranscript() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: transcriptsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transcripts'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useReprocessSummary() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: transcriptsApi.reprocess,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['transcript', id] })
      queryClient.invalidateQueries({ queryKey: ['transcripts'] })
    },
  })
}

export function useFilterOptions() {
  return useQuery({
    queryKey: ['filter-options'],
    queryFn: () => transcriptsApi.getFilterOptions(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}
